import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  TextField,
  Typography,
  Divider,
} from '@mui/material';
import { LoginPageStyles } from '../styles/LoginPageStyles';
import { useMutation, useQuery } from '@apollo/client';
import { GET_ME, updateCacheGetMe } from '../graphql/getMe';
import { SIGNUP } from '../graphql/signup';
import { LOGIN_WITH_GOOGLE } from '../graphql/login';
import { Role } from '../__generated__/graphql';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { CommonStyles } from '../styles/CommonStyles';
import VerificationModal from '../components/VerificationModal';
import { GoogleLogin } from '@react-oauth/google';
import { useNavigateHome } from '../hooks/useNavigateHome';

const Signup = () => {
  const [email, setEmail] = useState<string>('');
  const [name, setName] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [errors, setErrors] = useState({
    email: '',
    name: '',
    password: '',
  });
  const [generalError, setGeneralError] = useState<string>('');
  const [showVerificationModal, setShowVerificationModal] = useState(false);

  const { data: me, loading: meLoading } = useQuery(GET_ME);
  const { navigateHome } = useNavigateHome();
  const [signup, { loading: signupLoading, error }] = useMutation(SIGNUP, {
    variables: {
      email,
      name,
      password,
    },
    update(cache, { data: { signup } }) {
      updateCacheGetMe(cache, signup);
    },
  });

  const [loginWithGoogle, { loading: googleLoginLoading }] = useMutation(
    LOGIN_WITH_GOOGLE,
    {
      onCompleted: () => {
        navigateHome();
      },
      onError: (error) => {
        setGeneralError(error.message);
      },
      refetchQueries: [GET_ME],
    },
  );

  const navigate = useNavigate();
  useEffect(() => {
    if (me && !meLoading) {
      navigateHome();
    }
  }, [me, meLoading, navigateHome]);

  const validateForm = () => {
    const newErrors = {
      email: '',
      name: '',
      password: '',
    };
    let isValid = true;

    if (!email.trim()) {
      newErrors.email = 'Email is required';
      isValid = false;
    }
    if (!name.trim()) {
      newErrors.name = 'Name is required';
      isValid = false;
    }
    if (!password.trim()) {
      newErrors.password = 'Password is required';
      isValid = false;
    }

    setErrors(newErrors);
    return isValid;
  };

  const handleSignup = async (event: React.FormEvent) => {
    event.preventDefault();
    setGeneralError('');
    if (validateForm()) {
      try {
        await signup({
          variables: { createUserInput: { email, name, password } },
        });
        setShowVerificationModal(true);
      } catch (err: any) {
        console.error('Signup error:', err);
        if (err.message.includes('already exists')) {
          setGeneralError(
            'An account with this email already exists. Please use a different email or login.',
          );
        } else {
          setGeneralError('An error occurred during signup. Please try again.');
        }
      }
    }
  };

  const handleVerificationSuccess = () => {
    navigate('/dashboard');
  };

  if (error) {
    console.log('Signup error');
  }

  if (meLoading) {
    return (
      <Box sx={CommonStyles.loadingContainer}>
        <CircularProgress size={70} />
      </Box>
    );
  }

  const handleClickShowPassword = () => setShowPassword((show) => !show);

  const handleMouseDownPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
  };

  return (
    <Box sx={LoginPageStyles.mainContainer}>
      <Box
        sx={LoginPageStyles.loginContainer}
        component="form"
        onSubmit={handleSignup}
      >
        {/* Logo and Title */}
        <Box sx={LoginPageStyles.logoTitleRow}>
          <Box
            component="img"
            src="https://res.cloudinary.com/dh0eho4vx/image/upload/v1752986194/yin_yang_mpsxtn.png"
            alt="Yin Yang Logo"
            sx={LoginPageStyles.logo}
          />
          <Typography sx={LoginPageStyles.title}>Prep Qi</Typography>
        </Box>

        <Typography sx={LoginPageStyles.subtitle}>
          Join Prep Qi and start your TCM board exam preparation
        </Typography>

        {/* Google Login */}
        <Box sx={LoginPageStyles.googleButtonContainer}>
          <GoogleLogin
            onSuccess={(credentialResponse) => {
              if (credentialResponse.credential) {
                loginWithGoogle({
                  variables: { token: credentialResponse.credential },
                });
              }
            }}
            onError={() => {
              setGeneralError('Google login failed. Please try again.');
            }}
            text="continue_with"
            theme="outline"
            shape="pill"
            width="100%"
            size="large"
            containerProps={{
              style: {
                height: '48px',
                fontSize: '14px',
                fontWeight: '500',
                scale: '1.2',
              }
            }}
          />
        </Box>

        {/* Divider */}
        <Box sx={LoginPageStyles.dividerContainer}>
          <Divider sx={LoginPageStyles.divider} />
          <Typography sx={LoginPageStyles.dividerText}>or</Typography>
          <Divider sx={LoginPageStyles.divider} />
        </Box>

        {/* Name Field */}
        <TextField
          sx={LoginPageStyles.textField}
          label="Full Name"
          value={name}
          error={!!errors.name}
          helperText={errors.name}
          onChange={(event) => setName(event.target.value)}
        />

        {/* Email Field */}
        <TextField
          sx={LoginPageStyles.textField}
          label="Email"
          value={email}
          error={!!errors.email}
          helperText={errors.email}
          onChange={(event) => setEmail(event.target.value)}
        />

        {/* Password Field */}
        <FormControl sx={LoginPageStyles.textField}>
          <InputLabel htmlFor="outlined-adornment-password">
            Password
          </InputLabel>
          <OutlinedInput
            sx={LoginPageStyles.passwordField}
            label="Password"
            type={showPassword ? 'text' : 'password'}
            value={password}
            error={!!errors.password}
            endAdornment={
              <InputAdornment position="end">
                <IconButton
                  onClick={handleClickShowPassword}
                  onMouseDown={handleMouseDownPassword}
                  onMouseUp={handleMouseUpPassword}
                  edge="end"
                >
                  {showPassword ? <VisibilityOff /> : <Visibility />}
                </IconButton>
              </InputAdornment>
            }
            onChange={(event) => setPassword(event.target.value)}
          />
          {errors.password && (
            <Typography color="error" variant="caption" sx={{ mt: 1 }}>
              {errors.password}
            </Typography>
          )}
        </FormControl>

        {/* Signup Button */}
        <Button
          sx={LoginPageStyles.button}
          type="submit"
          variant="contained"
          disabled={signupLoading}
        >
          {signupLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            'Create Account'
          )}
        </Button>

        {/* Error Message */}
        {generalError && (
          <Typography sx={LoginPageStyles.errorText}>{generalError}</Typography>
        )}

        {/* Login Link */}
        <Button
          sx={LoginPageStyles.signupButton}
          variant="outlined"
          onClick={() => navigate('/login')}
        >
          Already have an account? Sign in!
        </Button>
      </Box>

      <VerificationModal
        open={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        email={email}
        onVerificationSuccess={handleVerificationSuccess}
      />
    </Box>
  );
};

export default Signup;
